import Backbone from "backbone";
import _ from "underscore";

// All types of dispatcher events. Keep string value b/c there are chances it got left behind somewhere
export enum DispatcherEvents {
    All = "all",

    // Triggered when user notifications change within the app
    NotificationsChange = "notifications:change",

    // Triggered when the API object requires new credentials
    ApiNeedsCredentials = "api:needs_credentials",

    // Triggered when workflow is complete
    CompleteWorkflow = "complete:workflow",

    // Triggered when a step of the workflow is completed
    CompleteWorkflowStep = "complete:workflow-step",

    // Triggered when address is updated in dvir/view
    DvirUpdatedAddress = "update:dvir-address",

    // Triggered when the active (primary) driver is switched
    DriverSwitch = "driver:switch",

    // Triggered when HOS rules have been computed {ruleResult}
    HosRuleCompute = "hos:rule-compute",

    // Triggered to indicate the lock screen view is going to be activated
    LockscreenWillToggle = "lockscreen:willToggle",

    // Triggered when the screen locker determines that the lock screen view needs to be activated
    LockscreenToggle = "lockscreen:toggle",

    // Called when we receive login credentials after calling "authenticate()" against the server
    LoginCredentials = "login:credentials",

    // Called when local storage size has reached its limit
    LocalStorageFull = "localstorage:full",

    // When new messages are added client side
    MessagesChange = "messages:change",

    // Triggered when native receives an OpenCab broadcast
    OpenCab = "opencab",

    // Triggered when gps detects position change {position, speed}
    PositionChange = "position:change",

    // Triggered by nativeContainer, but links up to `store` (true/false)
    StatusCharging = "status:charging",

    // Triggered when battery level changes. nativeContainer triggers, view and status listen (true/false)
    StatusBattery = "status:battery",

    // Triggered when app enters or leave foreground. Necessary for nativeContainer, nativeBackgroundListener and unit test
    StatusBackground = "status:background",

    // Triggered when the online/offline status of the app bridge is determined
    StatusOnline = "status:online",

    // Triggered after successfully completing sync+merge of DVIR logs
    SyncDvir = "sync:dvir",

    // Triggered after successfully completing sync+merge of HOS logs
    SyncHos = "sync:hos",

    // Triggered when coordinates cannot be found for a user inputted address (the address will be removed)
    SyncHosLocationInvalid = "sync:hos-location-invalid",

    // Triggered when the location needs to be entered for one or more logs
    SyncHosLocationManual = "sync:hos-location-manual",

    // Triggered after successfully completing sync+merge of shipment logs
    SyncShipments = "sync:shipments",

    // Triggered after successfully completing sync+merge of TextMessage logs
    SyncMessages = "sync:messages",

    SyncDriverChange = "sync:driverchange",

    SyncTrailerAttachments = "sync:trailerAttachments",

    SyncUserHosRuleSet = "sync:userhosruleset",

    SyncTrips = "sync:trips",

    // Triggered after a full sync has occurred (successful or otherwise)
    SyncComplete = "sync:complete",

    // Trigger when a user's details are updated from the server
    UserUpdate = "user:update",

    // Triggered when the current UI user is switched
    UserSwitch = "user:switch",

    // Triggered to logout. utils/session.js listens to this event and immediately starts logout process
    UserRequestLogout = "user:request_logout",

    // Triggered when the user's language has changed
    UserUpdateLanguage = "user:update-language",

    UserUpdateRuleset = "user:update-ruleset",

    // Triggered when a new vehicle is selected
    VehicleSelected = "vehicle:selected",

    // Triggered when current vehicle is removed from database
    VehicleRemoved = "vehicle:removed",

    // Triggered to run the Vehicle Proximity check
    RunVehicleProximity = "vehicle:run-proximity",

    // Triggered when a vehicle (or multiple) are candidates for proximity-based assignment
    VehicleProximity = "vehicle:proximity",

    // Triggered when a driver moves away from an assigned vehicle
    VehicleOutOfRange = "vehicle:out-of-range",

    // Triggered when the server acknowledges that a vehicle has been stopped for 5 minutes or PC has come to an end
    VehicleEndedStatus = "vehicle:endedstatus",

    // Triggered when a new page loads
    ViewPageloaded = "view:pageloaded",

    WarnOmittedDataOnDSL = "hos:warn-omitted-data-on-dsl",

    UserAddDSLForDriver = "user:add-dsl-for-driver",

    AddDSL = "add-dsl",

    UserSetDSLForDriver = "user:set-dsl-for-driver",

    UIUserMenu = "ui:userMenu",

    ErrorSendToErrorTracker = "error:send-to-error-tracker",

    ErrorHandleJS = "error:handle-js",

    ErrorHandleJSNative = "error:handle-js-native",

    ErrorHandleApi = "error:handle-api",

    ErrorHandleUnhandledRejection = "error:handle-unhandled-rejection",

    ProvideFeedback = "error:report-bug",

    LoggerLog = "logger:log",

    LoggerTime = "logger:time",

    LoggerTimeEnd = "logger:time-end",

    LoggerFeedback = "logger:feedback",

    WebViewLayerNewDutyStatusLog = "event:webview-layer-new-duty-status-log",

    DriveAppInteraction = "event:drive-app-interaction",

    RemoveUserSessions = "event:remove-user-sessions",

    NewIoxData = "event:new-iox-data",

    IoxBleError = "event:iox-ble-error",

    IoxAttached = "event:iox-device-attached",

    DvirLogSet = "event:dvir-log-set-from-server",

    HardwareIdCheck = "event:hardware-id-check",

    SystemSettingsUpdate = "event:system-settings-update",

    ExitInspectionMode = "event:exit-inspection-mode",

    HideToastMessage = "event:hide-toast-message",

    HideTopbarMessage = "event:hide-topbar-message",

    NewGpsData = "event:new-gps-data",

    NewLastFiveSecondAvgGpsData = "event:new-last-five-second-avg-gps-data",

    NewPositionDataForLockScreen = "event:new-position-data-for-lock-screen",

    ServiceWorkerRegistered = "service-worker:registered",

    ServiceWorkerUpdateFound = "service-worker:update-found",

    ServiceWorkerUpdateInstalling = "service-worker:update-installing",

    ServiceWorkerUpdateSuccess = "service-worker:update-success",

    ServiceWorkerUpdateActivated = "service-worker:update-activated",

    ServiceWorkerInstallSuccess = "service-worker:install-success",

    ImageAdded = "event:image-added",

    UpdateCameraIcon = "event:update-camera-icon",

    CheckUnverifiedRuleSetLogs = "event:ruleset-logs",

    ClockDrift = "event:clock-drift",

    CheckForCanadaTwoDayPopup = "hos:check-two-day-popup",

    VehicleChangedInExemption = "vehicle:changed-in-exemption"
}

export class Dispatcher implements Backbone.IEvents {

    constructor() {
        _.extend(this, Backbone.Events);
    }

    // Wrapping backbone's dispatcher with stricter typing on event names
    on(eventName: DispatcherEvents, callback?: Function, context?: any): any { return this.on(eventName, callback, context); }
    off(eventName?: DispatcherEvents, callback?: Function, context?: any): any { return this.off(eventName, callback, context); }
    trigger(eventName: DispatcherEvents, ...args: any[]): any { return this.trigger(eventName, ...args); }
    bind(eventName: DispatcherEvents, callback: Function, context?: any): any { return this.bind(eventName, callback, context); }
    unbind(_1?: DispatcherEvents, _2?: Function, _3?: any): any { return this.unbind(); }
    once(events: DispatcherEvents, callback: Function, context?: any): any { return this.once(events, callback, context); }
    listenTo(object: any, events: DispatcherEvents, callback: Function): any { return this.listenTo(object, events, callback); }
    listenToOnce(object: any, events: DispatcherEvents, callback: Function): any { return this.listenToOnce(object, events, callback); }
    stopListening(object?: any, events?: DispatcherEvents, callback?: Function): any { return this.stopListening(object, events, callback); }
}

export let dispatcher = new Dispatcher();
window.geotab.getSharedVariable("driveApp").dispatcher = dispatcher;
window.geotab.getSharedVariable("driveApp").dispatcherEvents = DispatcherEvents;